import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Users,
  Building,
  Clock,
  CheckCircle,
  AlertTriangle,
  PieChart,
  LineChart,
  Activity,
  Target,
  Award,
  Zap,
  Database,
  Globe,
  Mail,
  Bell,
  Settings,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  X,
  Save,
  Share2,
  Printer,
  Archive,
  Star,
  Bookmark,
  Tag,
  Layers,
  Grid,
  List,
  Maximize2,
  Minimize2,
  Play
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportResults, setReportResults] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    type: 'table',
    query: '',
    parameters: [],
    schedule: 'manual',
    recipients: [],
    isPublic: false
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <BarChart3 size={18} /> },
    { id: 'reports', label: 'Reports', icon: <FileText size={18} /> },
    { id: 'scheduled', label: 'Scheduled', icon: <Clock size={18} /> },
    { id: 'templates', label: 'Templates', icon: <Layers size={18} /> },
    { id: 'analytics', label: 'Analytics', icon: <TrendingUp size={18} /> }
  ];

  const reportCategories = [
    { value: 'assets', label: 'Asset Reports' },
    { value: 'contracts', label: 'Contract Reports' },
    { value: 'financial', label: 'Financial Reports' },
    { value: 'compliance', label: 'Compliance Reports' },
    { value: 'performance', label: 'Performance Reports' },
    { value: 'custom', label: 'Custom Reports' }
  ];

  const reportTypes = [
    { value: 'table', label: 'Table Report', icon: <Grid size={16} /> },
    { value: 'chart', label: 'Chart Report', icon: <BarChart3 size={16} /> },
    { value: 'dashboard', label: 'Dashboard', icon: <Activity size={16} /> },
    { value: 'summary', label: 'Summary Report', icon: <FileText size={16} /> }
  ];

  // Sample data for demonstration
  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const sampleReports = [
        {
          id: 1,
          name: 'Asset Utilization Report',
          description: 'Comprehensive report on asset usage and performance metrics',
          category: 'assets',
          type: 'table',
          status: 'active',
          lastRun: '2024-01-15T10:30:00Z',
          nextRun: '2024-01-16T10:30:00Z',
          createdBy: 'John Doe',
          createdAt: '2024-01-10T09:00:00Z',
          isPublic: true,
          schedule: 'daily',
          recipients: ['admin@company.com', 'manager@company.com']
        },
        {
          id: 2,
          name: 'Contract Renewal Analysis',
          description: 'Analysis of upcoming contract renewals and risk assessment',
          category: 'contracts',
          type: 'chart',
          status: 'active',
          lastRun: '2024-01-14T14:20:00Z',
          nextRun: '2024-01-17T14:20:00Z',
          createdBy: 'Jane Smith',
          createdAt: '2024-01-08T11:15:00Z',
          isPublic: false,
          schedule: 'weekly',
          recipients: ['legal@company.com']
        },
        {
          id: 3,
          name: 'Financial Performance Dashboard',
          description: 'Real-time financial metrics and KPIs dashboard',
          category: 'financial',
          type: 'dashboard',
          status: 'active',
          lastRun: '2024-01-15T08:00:00Z',
          nextRun: '2024-01-16T08:00:00Z',
          createdBy: 'Mike Johnson',
          createdAt: '2024-01-05T16:45:00Z',
          isPublic: true,
          schedule: 'daily',
          recipients: ['finance@company.com', 'ceo@company.com']
        },
        {
          id: 4,
          name: 'Compliance Status Report',
          description: 'Monthly compliance tracking and audit status',
          category: 'compliance',
          type: 'summary',
          status: 'draft',
          lastRun: null,
          nextRun: '2024-01-31T09:00:00Z',
          createdBy: 'Sarah Wilson',
          createdAt: '2024-01-12T13:30:00Z',
          isPublic: false,
          schedule: 'monthly',
          recipients: ['compliance@company.com']
        },
        {
          id: 5,
          name: 'Asset Maintenance Schedule',
          description: 'Upcoming maintenance tasks and resource allocation',
          category: 'assets',
          type: 'table',
          status: 'active',
          lastRun: '2024-01-15T06:00:00Z',
          nextRun: '2024-01-16T06:00:00Z',
          createdBy: 'Tom Brown',
          createdAt: '2024-01-03T10:20:00Z',
          isPublic: true,
          schedule: 'daily',
          recipients: ['maintenance@company.com']
        }
      ];
      setReports(sampleReports);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: sampleReports.length,
        itemsPerPage: 10
      });
      setLoading(false);
    }, 1000);
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
    // In real implementation, this would trigger API call with search parameter
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'category') {
      setCategoryFilter(value);
    } else if (filterType === 'status') {
      setStatusFilter(value);
    }
    // In real implementation, this would trigger API call with filter parameters
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setStatusFilter('');
    loadReports();
  };

  // Handle create report
  const handleCreateReport = () => {
    // In real implementation, this would call API to create report
    //console.log('Creating report:', formData);
    setShowCreateModal(false);
    resetForm();
    loadReports();
  };

  // Handle edit report
  const handleEditReport = () => {
    // In real implementation, this would call API to update report
    //console.log('Updating report:', selectedReport.id, formData);
    setShowEditModal(false);
    resetForm();
    setSelectedReport(null);
    loadReports();
  };

  // Handle delete report
  const handleDeleteReport = () => {
    // In real implementation, this would call API to delete report
    //console.log('Deleting report:', selectedReport.id);
    setShowDeleteModal(false);
    setSelectedReport(null);
    loadReports();
  };

  // Handle run report
  const handleRunReport = () => {
    // In real implementation, this would call API to run report
    //console.log('Running report:', selectedReport.id);
    setShowRunModal(false);
    setSelectedReport(null);
    // Simulate report results
    setReportResults({
      id: selectedReport.id,
      name: selectedReport.name,
      generatedAt: new Date().toISOString(),
      recordCount: 1250,
      data: [
        { id: 1, name: 'Sample Data 1', value: 100, status: 'Active' },
        { id: 2, name: 'Sample Data 2', value: 250, status: 'Pending' },
        { id: 3, name: 'Sample Data 3', value: 75, status: 'Completed' }
      ]
    });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      type: 'table',
      query: '',
      parameters: [],
      schedule: 'manual',
      recipients: [],
      isPublic: false
    });
  };

  // Open edit modal
  const openEditModal = (report) => {
    setSelectedReport(report);
    setFormData({
      name: report.name,
      description: report.description,
      category: report.category,
      type: report.type,
      query: '',
      parameters: [],
      schedule: report.schedule,
      recipients: report.recipients,
      isPublic: report.isPublic
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (report) => {
    setSelectedReport(report);
    setShowDeleteModal(true);
  };

  // Open run modal
  const openRunModal = (report) => {
    setSelectedReport(report);
    setShowRunModal(true);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: '#10b981', icon: CheckCircle, text: 'Active' },
      draft: { color: '#f59e0b', icon: AlertTriangle, text: 'Draft' },
      paused: { color: '#6b7280', icon: Clock, text: 'Paused' },
      error: { color: '#ef4444', icon: X, text: 'Error' }
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: `${config.color}20`,
        color: config.color
      }}>
        <Icon size={12} />
        {config.text}
      </span>
    );
  };

  // Get category badge
  const getCategoryBadge = (category) => {
    const categoryConfig = {
      assets: { color: '#3b82f6', text: 'Assets' },
      contracts: { color: '#8b5cf6', text: 'Contracts' },
      financial: { color: '#10b981', text: 'Financial' },
      compliance: { color: '#f59e0b', text: 'Compliance' },
      performance: { color: '#ef4444', text: 'Performance' },
      custom: { color: '#6b7280', text: 'Custom' }
    };

    const config = categoryConfig[category];

    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: `${config.color}20`,
        color: config.color
      }}>
        {config.text}
      </span>
    );
  };

  // Table columns for reports
  const reportColumns = [
    {
      key: 'name',
      label: 'Report Name',
      sortable: true,
      render: (report) => {
        if (!report) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: '#007B8F',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              <FileText size={20} />
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>
                {report.name}
                {report.isPublic && (
                  <span style={{
                    marginLeft: '8px',
                    padding: '2px 6px',
                    backgroundColor: '#dbeafe',
                    color: '#1d4ed8',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '500'
                  }}>
                    PUBLIC
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {report.description}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'category',
      label: 'Category',
      render: (report) => {
        if (!report) return null;
        return getCategoryBadge(report.category);
      }
    },
    {
      key: 'type',
      label: 'Type',
      render: (report) => {
        if (!report) return null;
        const typeConfig = reportTypes.find(t => t.value === report.type);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {typeConfig?.icon}
            <span style={{ fontSize: '14px' }}>{typeConfig?.label}</span>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (report) => {
        if (!report) return null;
        return getStatusBadge(report.status);
      }
    },
    {
      key: 'lastRun',
      label: 'Last Run',
      sortable: true,
      render: (report) => {
        if (!report) return null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Clock size={14} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '14px' }}>
              {report.lastRun ? new Date(report.lastRun).toLocaleDateString() : 'Never'}
            </span>
          </div>
        );
      }
    },
    {
      key: 'createdBy',
      label: 'Created By',
      sortable: true,
      render: (report) => {
        if (!report) return null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Users size={14} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '14px' }}>{report.createdBy}</span>
          </div>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (report) => {
        if (!report) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => openRunModal(report)}
              style={{
                padding: '6px',
                backgroundColor: '#dbeafe',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#1d4ed8'
              }}
              title="Run Report"
            >
              <Play size={16} />
            </button>
            
            <button
              onClick={() => openEditModal(report)}
              style={{
                padding: '6px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#6b7280'
              }}
              title="Edit Report"
            >
              <Edit size={16} />
            </button>

            <button
              onClick={() => openDeleteModal(report)}
              style={{
                padding: '6px',
                backgroundColor: '#fef2f2',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#dc2626'
              }}
              title="Delete Report"
            >
              <Trash2 size={16} />
            </button>
          </div>
        );
      }
    }
  ];

  return (
    <div style={{
      padding: '24px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#1f2937',
          margin: '0 0 8px 0',
          background: 'linear-gradient(135deg, #007B8F 0%, #4A9BA8 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Reports & Analytics
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          margin: 0
        }}>
          Generate, schedule, and manage comprehensive reports and analytics
        </p>
      </div>

      {/* Tabbed Navigation */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          gap: '0'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                color: activeTab === tab.id ? '#007B8F' : '#6b7280',
                borderBottom: activeTab === tab.id ? '3px solid #007B8F' : '3px solid transparent',
                transition: 'all 0.15s ease-in-out',
                fontWeight: '500',
                fontSize: '14px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                position: 'relative',
                marginBottom: '-1px'
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Key Metrics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FileText size={24} style={{ color: '#1d4ed8' }} />
                </div>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#10b981',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  <TrendingUp size={16} />
                  +12%
                </span>
              </div>
              <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '0 0 4px 0' }}>
                24
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Total Reports
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#d1fae5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Activity size={24} style={{ color: '#059669' }} />
                </div>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#10b981',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  <TrendingUp size={16} />
                  +8%
                </span>
              </div>
              <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '0 0 4px 0' }}>
                18
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Active Reports
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#fef3c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Clock size={24} style={{ color: '#d97706' }} />
                </div>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#ef4444',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  <TrendingDown size={16} />
                  -3%
                </span>
              </div>
              <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '0 0 4px 0' }}>
                156
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Reports Run Today
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#f3e8ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Users size={24} style={{ color: '#7c3aed' }} />
                </div>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#10b981',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  <TrendingUp size={16} />
                  +15%
                </span>
              </div>
              <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '0 0 4px 0' }}>
                8
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Scheduled Reports
              </p>
            </div>
          </div>

          {/* Recent Reports */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                Recent Reports
              </h3>
              <button style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#007B8F',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
              }}>
                <Plus size={16} />
                Create Report
              </button>
            </div>
            <DataTable
              data={reports.slice(0, 5)}
              columns={reportColumns}
              loading={false}
              pagination={false}
              searchable={false}
              sortable={true}
              exportable={false}
              selectable={false}
            />
          </div>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div>
          {/* Controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '24px',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              {/* Search */}
              <div style={{ position: 'relative' }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6b7280'
                }} />
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{
                    padding: '10px 12px 10px 40px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    width: '300px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                />
              </div>

              {/* Category Filter */}
              <select
                value={categoryFilter}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <option value="">All Categories</option>
                {reportCategories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="paused">Paused</option>
                <option value="error">Error</option>
              </select>

              <button
                onClick={clearFilters}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <Filter size={16} />
                Clear Filters
              </button>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <button
                onClick={() => loadReports()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: 'white',
                  color: '#007B8F',
                  border: '1px solid #007B8F',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <RefreshCw size={16} />
                Refresh
              </button>

              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: '#007B8F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <Plus size={16} />
                Create Report
              </button>
            </div>
          </div>

          {/* Data Table */}
          <DataTable
            data={reports}
            columns={reportColumns}
            loading={loading}
            pagination={pagination}
            onPageChange={loadReports}
            searchable={false}
            sortable={true}
            exportable={true}
            selectable={false}
          />
        </div>
      )}

      {/* Other Tabs */}
      {activeTab !== 'overview' && activeTab !== 'reports' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '40px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto'
          }}>
            <BarChart3 size={32} style={{ color: '#6b7280' }} />
          </div>
          <h3 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#1f2937',
            margin: '0 0 12px 0'
          }}>
            {tabs.find(tab => tab.id === activeTab)?.label}
          </h3>
          <p style={{
            fontSize: '16px',
            color: '#6b7280',
            margin: '0 0 24px 0',
            maxWidth: '500px',
            margin: '0 auto 24px auto'
          }}>
            {activeTab === 'scheduled' && 'Manage scheduled reports and automation settings.'}
            {activeTab === 'templates' && 'Browse and use report templates for quick report creation.'}
            {activeTab === 'analytics' && 'Advanced analytics and insights from your report data.'}
          </p>
          <button style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            backgroundColor: '#007B8F',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
            margin: '0 auto'
          }}>
            <BarChart3 size={20} />
            Configure {tabs.find(tab => tab.id === activeTab)?.label}
          </button>
        </div>
      )}

      {/* Create Report Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0'
            }}>
              Create New Report
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Report Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  <option value="">Select Category</option>
                  {reportCategories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Report Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  {reportTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateReport}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007B8F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Create Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedReport(null);
        }}
        onConfirm={handleDeleteReport}
        title="Delete Report"
        message={`Are you sure you want to delete the report "${selectedReport?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Run Report Modal */}
      {showRunModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '500px'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0'
            }}>
              Run Report
            </h3>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              margin: '0 0 24px 0'
            }}>
              Are you sure you want to run the report "{selectedReport?.name}"? This will generate a new report with current data.
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setShowRunModal(false);
                  setSelectedReport(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRunReport}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007B8F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Run Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
